import { create } from "zustand";
import type { AIModel } from "@/features/ai-model-selection/store/ai-model-store";
import { useAIModelStore } from "@/features/ai-model-selection/store/ai-model-store";
import { openWizardFromAssistantPayload } from "@/features/wizard/public-api";

export interface AssistantCreativePayload {
  type: "CREATIVE_GENERATION";
  // Tek model ya da model listesi gelebilir; şu an tekli model akışını destekleyeceğiz.
  model?: AIModel;
  models?: AIModel[];
  // Geleceğe dönük ek context alanları
  messageId?: string;
  userMessageText?: string;
}

interface CreativeGenerationState {
  isSelectingModel: boolean;
  assistantContext: AssistantCreativePayload | null;
  queue: AssistantCreativePayload[];

  // Actions
  openFromAssistant: (payload: AssistantCreativePayload) => void;
  handleModelSelect: (model: AIModel) => void;
  processNext: () => void;
  reset: () => void;
}

export const useCreativeGenerationStore = create<CreativeGenerationState>(
  (set, get) => ({
    isSelectingModel: false,
    assistantContext: null,
    queue: [],

    openFromAssistant: (payload) => {
      // Eğer zaten seçim açık ise sıraya al
      const state = get();

      // ❌ DEPRECATED: wizardOpen check kaldırıldı - artık wizard modal yok
      if (state.isSelectingModel) {
        set({ queue: [...state.queue, payload] });
        return;
      }
      // External model override kullanımı: payload.models varsa modal'a bu listeyi enjekte et
      const aiModelStore = useAIModelStore.getState();

      // External model set et (override)
      if (payload.models && payload.models.length > 0) {
        aiModelStore.setExternalModels(payload.models);
      } else if (payload.model) {
        // Tek model geldiyse listeye dönüştür
        aiModelStore.setExternalModels([payload.model]);
      }

      set({ assistantContext: payload, isSelectingModel: true });

      // Modal'ı aç ve seçim callback'ini bağla
      aiModelStore.openModal(
        (model) => get().handleModelSelect(model),
        payload.models && payload.models.length > 0
          ? payload.models
          : payload.model
          ? [payload.model]
          : null
      );
    },

    handleModelSelect: (model) => {
      const { assistantContext } = get();

      // Model seçildiğinde wizard'ı başlat
      if (assistantContext) {
        // Mevcut external model listesini taşı (geri dönülebilmesi için)
        const aiModelStoreRef = useAIModelStore.getState();
        const externalList = aiModelStoreRef.externalModels || null;
        openWizardFromAssistantPayload({
          model,
          source: "creative-generation",
          messageId: assistantContext.messageId,
          initialMessage: assistantContext.userMessageText,
          models: externalList || undefined,
        });
      }

      // Seçim bitti, modal external override'ı temizle
      const aiModelStore = useAIModelStore.getState();
      aiModelStore.setExternalModels(null);

      set({ isSelectingModel: false, assistantContext: null });
      get().processNext();
    },

    processNext: () => {
      const state = get();
      if (state.queue.length === 0) return;
      const [next, ...rest] = state.queue;
      set({ queue: rest });
      // Bir sonrakini başlat
      get().openFromAssistant(next);
    },

    reset: () =>
      set({ isSelectingModel: false, assistantContext: null, queue: [] }),
  })
);

// Otomatik geri yükleme
// CG seçim aşaması için draft kullanılmıyor; restore mekanizması devre dışı
